import type { ContentHref } from '@generalTypes/apiTypes';
import { getAngularService } from '@kathondvla/react-shared-components/src/helpers/angularReactHelper';
import type { EditAsideKrcGoalRelations } from '@nodeTypeConfig/configTypes';
import { Modal } from '@kathondvla/react-shared-components/src/components';
import { addRelationAction, removeRelationAction } from '@store/actions/documentActions';
import { any } from 'prop-types';
import type React from 'react';
import { useRef } from 'react';
import { useDispatch } from 'react-redux';
import ModalErrorBoundary from '@UI/modals/ModalErrorBoundary';
import KrcGoalRelationModal, {
  type KrcGoalRelationModalSubmit,
} from '@UI/modals/KrcGoalRelationModal';
import { useEditHref } from '../../../hooks/UseEditHref';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import {
  type KrcGoalRelationItem,
  getContentRelationTypeForKrcRelation,
  selectKrcGoalRelations,
} from './asideKrcGoalRelationsSelectors';
import { getResourceKey } from '@store/helpers/documentHelpers';
import { selectRootHref } from '@newStore/documentUI/documentUISelectors';
import { krcCurriculumRoot } from '@newStore/referenceFrames/referenceFrames';

const buildRelationPayload = (
  currentGoalHref: ContentHref,
  relationType: EditAsideKrcGoalRelations['options']['relationType'],
  relation: KrcGoalRelationModalSubmit,
) => {
  const contentRelationType = getContentRelationTypeForKrcRelation(relationType);

  if (relationType === 'PRECEDES_BEFORE') {
    return {
      relationtype: contentRelationType,
      strength: 'MEDIUM',
      from: {
        href: relation.targetHref,
      },
      to: {
        href: currentGoalHref,
      },
    };
  }

  return {
    relationtype: contentRelationType,
    strength: 'MEDIUM',
    from: {
      href: currentGoalHref,
    },
    to: {
      href: relation.targetHref,
    },
  };
};

const AsideKrcGoalRelations: React.FC<{
  config: EditAsideKrcGoalRelations;
  readonly: boolean;
}> = ({ config, readonly }) => {
  const editHref = useEditHref();
  const rootHref = useTypedSelector(selectRootHref);
  const dispatch = useDispatch();
  const confirmationService = getAngularService('ConfirmationService');
  const relationModalRef = useRef<Modal>();

  const relations = useTypedSelector((state) => selectKrcGoalRelations(state, editHref, config));

  const openRelationModal = (relation?: KrcGoalRelationItem) => {
    relationModalRef.current?.open({
      relation,
      relationType: config.options.relationType,
      title: config.label,
      onSubmit: (response: KrcGoalRelationModalSubmit | undefined) => {
        if (!response) {
          return;
        }

        if (relation) {
          dispatch(removeRelationAction(relation.key));
        }

        dispatch(
          addRelationAction(buildRelationPayload(editHref, config.options.relationType, response)),
        );
      },
    });
  };

  const deleteRelation = async (relation: KrcGoalRelationItem) => {
    const confirmed = await confirmationService.confirmDeletion();
    if (!confirmed) {
      return;
    }

    dispatch(removeRelationAction(relation.key));
  };

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <div className="panel-controls panel-controls-left">
          <h3 className="panel-title">{config.label}</h3>
        </div>
        <div className="panel-controls panel-controls-right">
          {!readonly && (
            <div className="btn-toolbar">
              <a
                className="btn btn-default"
                onClick={() => {
                  openRelationModal();
                }}
              >
                <span className="glyphicon glyphicon-add" />
                <span>Voeg toe</span>
              </a>
            </div>
          )}
        </div>
      </div>

      <div className="panel-body">
        <div className="structure">
          {relations.map((relation) => {
            const isDisciplineDocument = rootHref !== krcCurriculumRoot;
            const isExternal = isDisciplineDocument && relation.disciplineHref !== rootHref;
            const documentKey =
              isDisciplineDocument && relation.disciplineHref
                ? getResourceKey(relation.disciplineHref)
                : getResourceKey(rootHref);
            return (
              <div className="row table-row" key={relation.key}>
                {!relation.isLoaded ? (
                  <>
                    <div className="col-md-2">
                      <div className="shimmer narrowLine" />
                    </div>
                    <div className="col-md-10">
                      <div className="shimmer narrowLine" />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-md-2" style={{ fontWeight: 'bold' }}>
                      {relation.disciplineHref ? (
                        <a
                          href={`/edit/${documentKey}/${getResourceKey(relation.targetHref)}`}
                          title={`Ga naar de bewerking van het doel ${relation.goalIdentifier} - ${relation.title}`}
                          {...(isExternal && { target: '_blank', rel: 'noopener noreferrer' })}
                        >
                          {relation.goalIdentifier}
                          {isExternal && (
                            <span
                              className="glyphicon glyphicon-new-window"
                              style={{ marginLeft: 4, fontSize: '0.8em' }}
                            />
                          )}
                        </a>
                      ) : (
                        <span>{relation.goalIdentifier}</span>
                      )}
                    </div>
                    <div className="col-md-10">
                      <div>{relation.title}</div>
                    </div>
                  </>
                )}
                {!readonly && (
                  <div className="btn-group btn-group-xs row-actions">
                    <button
                      type="button"
                      className="btn btn-default"
                      onClick={() => {
                        openRelationModal(relation);
                      }}
                    >
                      <span className="glyphicon glyphicon-edit" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-default"
                      onClick={() => {
                        deleteRelation(relation);
                      }}
                    >
                      <span className="glyphicon glyphicon-delete" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Modal
        ref={relationModalRef}
        size="sm"
        component={<KrcGoalRelationModal />}
        errorBoundaryComponent={ModalErrorBoundary}
      />
    </div>
  );
};

AsideKrcGoalRelations.displayName = 'AsideKrcGoalRelations';

AsideKrcGoalRelations.propTypes = {
  config: any,
  readonly: any,
};

export default AsideKrcGoalRelations;
